import actionConstants from '../constants/';

const defaultState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  errorCode: 0,
  errorMessage: '',
};

const subscriptionListState = {
  ...defaultState,
  data: [],
};

const presentationListState = {
  ...defaultState,
  data: [],
  totalElements: 0,
};

const getPresentationRecords = (body: any = {}): any[] => {
  if (Array.isArray(body)) return body;

  const records =
    body.presentations ??
    body.data?.presentations ??
    body.data?.content ??
    body.content ??
    body.data ??
    [];

  return Array.isArray(records) ? records : [];
};

const getPresentationTotalElements = (
  body: any = {},
  records: any[] = []
): number =>
  body.page?.total_elements ??
  body.page?.totalElements ??
  body.data?.page?.total_elements ??
  body.data?.page?.totalElements ??
  body.total_elements ??
  body.totalElements ??
  records.length;

const pauseRevokeSubscriptionState = {
  ...defaultState,
};

const updateSubscriptionState = {
  ...defaultState,
};

const updateMaxPaymentLinkState = {
  ...defaultState,
  isSuccess: false,
  isFailure: false,
};

const merchantSettingsFlagState = {
  isLoading: false,
  isSuccess: false,
  isFailure: false,
  isAllowedForConvenienceFeeRefund: false as boolean,
};

const setMerchantSettingsFlagState = {
    ...defaultState,
};
const uploadSubscriptionBulkState = {
  ...defaultState,
};

const bulkUploadHistoryState = {
  ...defaultState,
  data: [],
  totalRecords: 0,
};

const mapBulkUploadJob = (job: any = {}) => ({
  ...job,
  id: job.id || job.job_id,
  jobId: job.jobId || job.job_id,
  fileName: job.fileName || job.file_name,
  batchDescription: job.batchDescription || job.description,
  submittedAt: job.submittedAt || job.submitted_at,
  createdOn: job.createdOn || job.submitted_at,
  recordsTotal: job.recordsTotal ?? job.records_total,
  recordsSucceeded: job.recordsSucceeded ?? job.records_succeeded,
  recordsFailed: job.recordsFailed ?? job.records_failed,
  uploadedBy: job.uploadedBy || job.user_id,
  jobType: job.jobType || job.job_type || job.type,
});

const subscriptionLinksListState = {
  ...defaultState,
  data: [],
  totalElements: 0,
};

const getAmountValue = (amount: any) => (
  amount && typeof amount === 'object' && 'value' in amount ? amount.value : amount
);

const getCustomerName = (customer: any = {}) => (
  customer.name ||
  [customer.first_name, customer.last_name].filter(Boolean).join(' ') ||
  undefined
);

const mapSubscriptionLink = (link: any = {}) => {
  const planDetails = link.plan_details || link.plan || {};
  const customer = link.customer || {};
  const maxAmount = getAmountValue(
    link.maximum_amount ||
    link.max_amount ||
    link.max_limit_amount ||
    planDetails.maximum_amount ||
    planDetails.max_amount ||
    planDetails.max_limit_amount,
  );
  const amount = getAmountValue(link.amount || planDetails.amount);
  const upfrontAmount = getAmountValue(
    link.upfront_amount ||
    link.upfront_debit_amount ||
    link.initial_debit_amount ||
    planDetails.upfront_amount ||
    planDetails.upfront_debit_amount ||
    planDetails.initial_debit_amount,
  );

  return {
    ...link,
    id: link.id || link.subscription_link_id || link.link_id,
    short_url:
      link.short_url ||
      link.subscription_link_url ||
      link.subscription_link ||
      link.link_url ||
      link.url,
    merchant_subscription_link_reference:
      link.merchant_subscription_link_reference ||
      link.merchant_subscription_reference ||
      link.merchant_order_reference ||
      link.order_reference,
    merchant_order_reference:
      link.merchant_order_reference ||
      link.order_reference,
    plan_name: link.plan_name || planDetails.plan_name,
    plan_id: link.plan_id || planDetails.plan_id,
    frequency: link.frequency || link.frequency_type || planDetails.frequency || planDetails.frequency_type,
    amount: amount || link.amount,
    amount_value: amount,
    amount_currency:
      link.amount_currency ||
      link.amount?.currency ||
      planDetails.amount?.currency,
    maximum_amount: maxAmount,
    max_amount: maxAmount,
    customer_mobile_masked:
      link.customer_mobile_masked ||
      link.mobile_number ||
      link.phone_number ||
      customer.mobile_number,
    customer_email_masked:
      link.customer_email_masked ||
      link.email_id ||
      link.email ||
      customer.email_id,
    customer_name: link.customer_name || getCustomerName(customer),
    email_id: link.email_id || customer.email_id,
    phone_number: link.phone_number || link.mobile_number || customer.mobile_number,
    payment_mode: link.payment_mode || link.paymentMode,
    upfront_amount: upfrontAmount,
    free_trial_days:
      link.free_trial_days ??
      link.trial_period_in_days ??
      planDetails.free_trial_days ??
      planDetails.trial_period_in_days,
    reminders_sent: link.reminders_sent ?? link.reminder_count,
    reminder_date: link.reminder_date || link.last_reminder_date || link.last_reminded_at,
    start_date: link.start_date || planDetails.start_date,
    end_date: link.end_date || planDetails.end_date,
    created_at: link.created_at || link.created_on,
    expiry_date: link.expiry_date || link.link_expiry_date,
  };
};

const createSubscriptionLinkState = {
  ...defaultState,
};

const subscriptionLinkDetailState = {
  ...defaultState,
  data: null,
};

const cancelSubscriptionLinkState = {
  ...defaultState,
};

const duplicateSubscriptionLinkState = {
  ...defaultState,
};

const notifySubscriptionLinkState = {
  ...defaultState,
};

const exportSubscriptionLinksState = {
  ...defaultState,
};

const initialState = {
  subscriptionListState: { ...subscriptionListState },
  presentationListState: { ...presentationListState },
  pauseRevokeSubscriptionState: { ...pauseRevokeSubscriptionState },
  updateSubscriptionState: {...updateSubscriptionState},
  updateMaxPaymentLinkState: {...updateMaxPaymentLinkState},
  merchantSettingsFlagState: { ...merchantSettingsFlagState },
  setMerchantSettingsFlagState: { ...setMerchantSettingsFlagState },
  uploadSubscriptionBulkState: {...uploadSubscriptionBulkState},
  bulkUploadHistoryState: {...bulkUploadHistoryState},
  subscriptionLinksListState: {...subscriptionLinksListState},
  createSubscriptionLinkState: {...createSubscriptionLinkState},
  subscriptionLinkDetailState: {...subscriptionLinkDetailState},
  cancelSubscriptionLinkState: {...cancelSubscriptionLinkState},
  duplicateSubscriptionLinkState: {...duplicateSubscriptionLinkState},
  notifySubscriptionLinkState: {...notifySubscriptionLinkState},
  exportSubscriptionLinksState: {...exportSubscriptionLinksState},
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function subscriptionReducer(state = initialState, action) {
  switch (action.type) {
    case actionConstants.UPDATE_MAX_PAYMENT_LINK_REQUEST:
      return {
        ...state,
        updateMaxPaymentLinkState: {
          ...state.updateMaxPaymentLinkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };
    case actionConstants.UPDATE_MAX_PAYMENT_LINK_SUCCESS:
      return {
        ...state,
        updateMaxPaymentLinkState: {
          ...state.updateMaxPaymentLinkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };
    case actionConstants.UPDATE_MAX_PAYMENT_LINK_FAILURE:
      return {
        ...state,
        updateMaxPaymentLinkState: {
          ...state.updateMaxPaymentLinkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.UPDATE_MAX_PAYMENT_LINK_CLEAR:
      return {
        ...state,
        updateMaxPaymentLinkState: updateMaxPaymentLinkState,
      };

    case actionConstants.GET_MERCHANT_SETTINGS_FLAG_REQUEST:
      return {
        ...state,
        merchantSettingsFlagState: {
          ...state.merchantSettingsFlagState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };
    case actionConstants.GET_MERCHANT_SETTINGS_FLAG_SUCCESS:
      return {
        ...state,
        merchantSettingsFlagState: {
          ...state.merchantSettingsFlagState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          isAllowedForConvenienceFeeRefund:
            action.payload.body?.isAllowedForConvenienceFeeRefund ?? false,
        },
      };
    case actionConstants.GET_MERCHANT_SETTINGS_FLAG_FAILURE:
      return {
        ...state,
        merchantSettingsFlagState: {
          ...state.merchantSettingsFlagState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
        },
      };

    case actionConstants.SET_MERCHANT_SETTINGS_FLAG_REQUEST:
      return {
        ...state,
        setMerchantSettingsFlagState: {
          ...state.setMerchantSettingsFlagState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
        },
      };
    case actionConstants.SET_MERCHANT_SETTINGS_FLAG_SUCCESS:
      return {
        ...state,
        setMerchantSettingsFlagState: {
          ...state.setMerchantSettingsFlagState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
        merchantSettingsFlagState: {
          ...state.merchantSettingsFlagState,
          isAllowedForConvenienceFeeRefund:
            action.payload.extra?.isAllowedForConvenienceFeeRefund ??
            state.merchantSettingsFlagState.isAllowedForConvenienceFeeRefund,
        },
      };
    case actionConstants.SET_MERCHANT_SETTINGS_FLAG_FAILURE:
      return {
        ...state,
        setMerchantSettingsFlagState: {
          ...state.setMerchantSettingsFlagState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
        },
      };
    case actionConstants.SET_MERCHANT_SETTINGS_FLAG_CLEAR:
      return {
        ...state,
        setMerchantSettingsFlagState: { ...setMerchantSettingsFlagState },
      };

    case actionConstants.GET_SUBSCRIPTION_LIST_REQUEST:
      return {
        ...state,
        subscriptionListState: {
          ...state.subscriptionListState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };

    case actionConstants.GET_SUBSCRIPTION_LIST_SUCCESS:
      return {
        ...state,
        subscriptionListState: {
          ...state.subscriptionListState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: action.payload.body?.subscriptions || [],
          totalElements: action.payload.body?.page?.total_elements || 0,
        },
      };

    case actionConstants.GET_SUBSCRIPTION_LIST_FAILURE:
      return {
        ...state,
        subscriptionListState: {
          ...state.subscriptionListState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body.code,
          data: [],
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };

    case actionConstants.GET_SUBSCRIPTION_LIST_CLEAR:
      return {
        ...state,
        subscriptionListState: subscriptionListState,
      };

    case actionConstants.GET_SUBSCRIPTION_PRESENTATIONS_REQUEST:
      return {
        ...state,
        presentationListState: {
          ...state.presentationListState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };

    case actionConstants.GET_SUBSCRIPTION_PRESENTATIONS_SUCCESS: {
      const responseBody = action.payload.body || {};
      const data = getPresentationRecords(responseBody);

      return {
        ...state,
        presentationListState: {
          ...state.presentationListState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data,
          totalElements: getPresentationTotalElements(responseBody, data),
        },
      };
    }

    case actionConstants.GET_SUBSCRIPTION_PRESENTATIONS_FAILURE:
      return {
        ...state,
        presentationListState: {
          ...state.presentationListState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          data: [],
          totalElements: 0,
          errorMessage:
            action.payload.body?.error_details?.error
              ?.additional_error_details?.reason ||
            action.payload.body?.error_details?.error?.message ||
            action.payload.body?.message,
        },
      };

    case actionConstants.GET_SUBSCRIPTION_PRESENTATIONS_CLEAR:
      return {
        ...state,
        presentationListState,
      };

    case actionConstants.PAUSE_REVOKE_SUBSCRIPTION_REQUEST:
      return {
        ...state,
        pauseRevokeSubscriptionState: {
          ...state.pauseRevokeSubscriptionState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };

    case actionConstants.PAUSE_REVOKE_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        pauseRevokeSubscriptionState: {
          ...state.pauseRevokeSubscriptionState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };

    case actionConstants.PAUSE_REVOKE_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        pauseRevokeSubscriptionState: {
          ...state.pauseRevokeSubscriptionState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };

    case actionConstants.PAUSE_REVOKE_SUBSCRIPTION_CLEAR:
      return {
        ...state,
        pauseRevokeSubscriptionState: pauseRevokeSubscriptionState,
      };

    case actionConstants.PUT_UPDATE_MERCHANT_SUBSCRIPTION_REQUEST:
      return{
        ...state,
        updateSubscriptionState: {
          ...state.updateSubscriptionState,
          isLoading: true,
        }
      };

    case actionConstants.PUT_UPDATE_MERCHANT_SUBSCRIPTION_SUCCESS:
      return {
        ...state,
        updateSubscriptionState: {
          ...state.updateSubscriptionState,
          isSuccess: true,
        },
      };

    case actionConstants.PUT_UPDATE_MERCHANT_SUBSCRIPTION_FAILURE:
      return {
        ...state,
        updateSubscriptionState: {
          ...updateSubscriptionState,
          isFailure: true,
        },
      };

    case actionConstants.PUT_UPDATE_MERCHANT_SUBSCRIPTION_CLEAR:
      return {
        ...state,
        updateSubscriptionState: {
          ...updateSubscriptionState,
        },
      };

    case actionConstants.UPLOAD_SUBSCRIPTION_BULK_REQUEST:
      return {
        ...state,
        uploadSubscriptionBulkState: {
          ...state.uploadSubscriptionBulkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };

    case actionConstants.UPLOAD_SUBSCRIPTION_BULK_SUCCESS:
      return {
        ...state,
        uploadSubscriptionBulkState: {
          ...state.uploadSubscriptionBulkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };

    case actionConstants.UPLOAD_SUBSCRIPTION_BULK_FAILURE:
      return {
        ...state,
        uploadSubscriptionBulkState: {
          ...state.uploadSubscriptionBulkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };

    case actionConstants.UPLOAD_SUBSCRIPTION_BULK_CLEAR:
      return {
        ...state,
        uploadSubscriptionBulkState: uploadSubscriptionBulkState,
      };

    // Bulk Upload History cases
    case actionConstants.GET_BULK_UPLOAD_HISTORY_REQUEST:
      return {
        ...state,
        bulkUploadHistoryState: {
          ...state.bulkUploadHistoryState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorMessage: '',
        },
      };
    case actionConstants.GET_BULK_UPLOAD_HISTORY_SUCCESS: {
      const bulkUploadHistoryBody = action.payload?.body || {};
      const bulkUploadJobs = bulkUploadHistoryBody.jobs || [];
      return {
        ...state,
        bulkUploadHistoryState: {
          ...state.bulkUploadHistoryState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: bulkUploadJobs.map(mapBulkUploadJob),
          totalRecords:
            bulkUploadHistoryBody.total_jobs ||
            bulkUploadHistoryBody.totalRecords ||
            bulkUploadJobs.length ||
            0,
          errorMessage: '',
        },
      };
    }
    case actionConstants.GET_BULK_UPLOAD_HISTORY_FAILURE:
      return {
        ...state,
        bulkUploadHistoryState: {
          ...state.bulkUploadHistoryState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          data: [],
          totalRecords: 0,
          errorMessage: action.payload?.message || 'Failed to fetch bulk upload history',
        },
      };
    case actionConstants.GET_BULK_UPLOAD_HISTORY_CLEAR:
      return {
        ...state,
        bulkUploadHistoryState: {...bulkUploadHistoryState},
      };

    // Subscription Links List
    case actionConstants.GET_SUBSCRIPTION_LINKS_REQUEST:
      return {
        ...state,
        subscriptionLinksListState: {
          ...state.subscriptionLinksListState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorMessage: '',
        },
      };
    case actionConstants.GET_SUBSCRIPTION_LINKS_SUCCESS: {
      const subscriptionLinksBody = action.payload.body || {};
      const subscriptionLinks = subscriptionLinksBody.subscription_links || [];
      return {
        ...state,
        subscriptionLinksListState: {
          ...state.subscriptionLinksListState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: subscriptionLinks.map(mapSubscriptionLink),
          totalElements: subscriptionLinksBody.page?.total_elements || subscriptionLinks.length || 0,
        },
      };
    }
    case actionConstants.GET_SUBSCRIPTION_LINKS_FAILURE:
      return {
        ...state,
        subscriptionLinksListState: {
          ...state.subscriptionLinksListState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          data: [],
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.GET_SUBSCRIPTION_LINKS_CLEAR:
      return {
        ...state,
        subscriptionLinksListState: {...subscriptionLinksListState},
      };

    // Create Subscription Link
    case actionConstants.CREATE_SUBSCRIPTION_LINK_REQUEST:
      return {
        ...state,
        createSubscriptionLinkState: {
          ...state.createSubscriptionLinkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };
    case actionConstants.CREATE_SUBSCRIPTION_LINK_SUCCESS:
      return {
        ...state,
        createSubscriptionLinkState: {
          ...state.createSubscriptionLinkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          linkUrl: action.payload.body?.subscription_link
            || action.payload.body?.data?.subscription_link
            || action.payload.body?.subscription_link_url
            || '',
        },
      };
    case actionConstants.CREATE_SUBSCRIPTION_LINK_FAILURE:
      return {
        ...state,
        createSubscriptionLinkState: {
          ...state.createSubscriptionLinkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.CREATE_SUBSCRIPTION_LINK_CLEAR:
      return {
        ...state,
        createSubscriptionLinkState: {...createSubscriptionLinkState},
      };

    // Subscription Link Detail
    case actionConstants.GET_SUBSCRIPTION_LINK_DETAIL_REQUEST:
      return {
        ...state,
        subscriptionLinkDetailState: {
          ...state.subscriptionLinkDetailState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorMessage: '',
        },
      };
    case actionConstants.GET_SUBSCRIPTION_LINK_DETAIL_SUCCESS:
      return {
        ...state,
        subscriptionLinkDetailState: {
          ...state.subscriptionLinkDetailState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
          data: action.payload.body ? mapSubscriptionLink(action.payload.body) : null,
        },
      };
    case actionConstants.GET_SUBSCRIPTION_LINK_DETAIL_FAILURE:
      return {
        ...state,
        subscriptionLinkDetailState: {
          ...state.subscriptionLinkDetailState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          data: null,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.GET_SUBSCRIPTION_LINK_DETAIL_CLEAR:
      return {
        ...state,
        subscriptionLinkDetailState: {...subscriptionLinkDetailState},
      };

    // Cancel Subscription Link
    case actionConstants.CANCEL_SUBSCRIPTION_LINK_REQUEST:
      return {
        ...state,
        cancelSubscriptionLinkState: {
          ...state.cancelSubscriptionLinkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };
    case actionConstants.CANCEL_SUBSCRIPTION_LINK_SUCCESS:
      return {
        ...state,
        cancelSubscriptionLinkState: {
          ...state.cancelSubscriptionLinkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };
    case actionConstants.CANCEL_SUBSCRIPTION_LINK_FAILURE:
      return {
        ...state,
        cancelSubscriptionLinkState: {
          ...state.cancelSubscriptionLinkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.CANCEL_SUBSCRIPTION_LINK_CLEAR:
      return {
        ...state,
        cancelSubscriptionLinkState: {...cancelSubscriptionLinkState},
      };

    // Duplicate Subscription Link
    case actionConstants.DUPLICATE_SUBSCRIPTION_LINK_REQUEST:
      return {
        ...state,
        duplicateSubscriptionLinkState: {
          ...state.duplicateSubscriptionLinkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };
    case actionConstants.DUPLICATE_SUBSCRIPTION_LINK_SUCCESS:
      return {
        ...state,
        duplicateSubscriptionLinkState: {
          ...state.duplicateSubscriptionLinkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };
    case actionConstants.DUPLICATE_SUBSCRIPTION_LINK_FAILURE:
      return {
        ...state,
        duplicateSubscriptionLinkState: {
          ...state.duplicateSubscriptionLinkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.DUPLICATE_SUBSCRIPTION_LINK_CLEAR:
      return {
        ...state,
        duplicateSubscriptionLinkState: {...duplicateSubscriptionLinkState},
      };

    // Notify Subscription Link
    case actionConstants.NOTIFY_SUBSCRIPTION_LINK_REQUEST:
      return {
        ...state,
        notifySubscriptionLinkState: {
          ...state.notifySubscriptionLinkState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorCode: 0,
          errorMessage: '',
        },
      };
    case actionConstants.NOTIFY_SUBSCRIPTION_LINK_SUCCESS:
      return {
        ...state,
        notifySubscriptionLinkState: {
          ...state.notifySubscriptionLinkState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };
    case actionConstants.NOTIFY_SUBSCRIPTION_LINK_FAILURE:
      return {
        ...state,
        notifySubscriptionLinkState: {
          ...state.notifySubscriptionLinkState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.NOTIFY_SUBSCRIPTION_LINK_CLEAR:
      return {
        ...state,
        notifySubscriptionLinkState: {...notifySubscriptionLinkState},
      };

    // Export Subscription Links
    case actionConstants.EXPORT_SUBSCRIPTION_LINKS_REQUEST:
      return {
        ...state,
        exportSubscriptionLinksState: {
          ...state.exportSubscriptionLinksState,
          isLoading: true,
          isSuccess: false,
          isFailure: false,
          errorMessage: '',
        },
      };
    case actionConstants.EXPORT_SUBSCRIPTION_LINKS_SUCCESS:
      return {
        ...state,
        exportSubscriptionLinksState: {
          ...state.exportSubscriptionLinksState,
          isLoading: false,
          isSuccess: true,
          isFailure: false,
        },
      };
    case actionConstants.EXPORT_SUBSCRIPTION_LINKS_FAILURE:
      return {
        ...state,
        exportSubscriptionLinksState: {
          ...state.exportSubscriptionLinksState,
          isLoading: false,
          isSuccess: false,
          isFailure: true,
          errorCode: action.payload.body?.code,
          errorMessage: action.payload.body?.error_details?.error?.message || action.payload.body?.message,
        },
      };
    case actionConstants.EXPORT_SUBSCRIPTION_LINKS_CLEAR:
      return {
        ...state,
        exportSubscriptionLinksState: {...exportSubscriptionLinksState},
      };

    default:
      return state;
  }
}
